<template>
  <BaseWidget
    :class="['company-profile', { 'accountant-profile': isAccountantDashboard, incomplete: isIncomplete }]"
    :loading="loading"
    @refresh-data="refreshData"
    @set-config="setConfig"
    v-on="$listeners"
  >
    <template v-if="isAccountantDashboard && !isIncomplete" v-slot:header>
      <div class="title">
        <span class="title-icon"> </span>
        {{ $gettext('Your company profile') }}
      </div>
      <!-- <div class="description" v-translate>Profile information</div> -->
    </template>
    <template v-slot:content>
      <div class="company-profile-wrap">
        <template v-if="!currentUser.profile.active_email">
          <div class="incomplete-content-wrap">
            <div class="incomplete-content">
              <AnimationItem
                class="anim-warning"
                :animation="{ animationData: warningAnimation, loop: false }"
                :key="'anim_warning'"
              />
              <h3 class="mb-3">{{ $gettext("Your email isn't verified") }}</h3>
              <GekkoButton
                @click="sendVerificationEmail"
                :text="$gettext('Send me an email')"
                :loading="isWaitingForResponse"
                :class="isWaitingForResponse ? 'loading-spinner-button' : ''"
              />
            </div>
          </div>
        </template>
        <template v-else-if="!loading && isIncomplete" @click="widgetClick">
          <div class="incomplete-content-wrap">
            <div class="incomplete-content">
              <AnimationItem
                class="anim-warning"
                :animation="{ animationData: warningAnimation, loop: false }"
                :key="'anim_warning'"
              />
              <div v-if="isAccountantDashboard" class="mb-3">
                <h4>{{ $gettext('Profile incomplete') }}</h4>
                <small>
                  {{ $gettext('Your company is missing:') }}
                  <strong>{{ accountantCompanyMissingFields.map((i) => i.label).join(', ') }}</strong>
                </small>
              </div>
              <h3 v-else class="mb-3">{{ $gettext('Company profile incomplete') }}</h3>
              <GekkoButton v-if="progress < 100" @click="openWizard" :text="$gettext('Finish account setup')" />
            </div>
          </div>
        </template>
        <template v-else-if="!loading && !isIncomplete" @click="widgetClick">
          <div class="complete-content-wrap" @click="gotoSettings">
            <div class="complete-content-wrap--logo">
              <img :src="currentCompany.logo" :alt="currentCompany.name" />
            </div>
            <h3 class="complete-content-wrap--title">{{ currentCompany.name }}</h3>
            <div class="complete-content-wrap--description">{{ currentCompany.address }} {{ currentCompany.city }}</div>
          </div>
        </template>
      </div>
    </template>
  </BaseWidget>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import BaseWidget from '@/components/dashboard/widgets/BaseWidget'
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as successAnimation from '@/css/animations/success.json'
import * as warningAnimation from '@/css/animations/warning.json'
import ImportKvkModal from '@/components/dashboard/ui/ImportKvkModal.vue'
import GekkoButton from '@/components/GekkoButton'
import DoughnutChart from '@/components/dashboard/charts/Doughnut'
import ProfileService from '@/store/services/profile.service'

export default {
  name: 'company-profile-widget',
  components: {
    BaseWidget,
    AnimationItem,
    ImportKvkModal,
    GekkoButton,
    DoughnutChart,
  },
  props: {
    widget: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: true,
      title: '',
      subtitle: '',
      config: { status: '' },
      filters: {},
      successAnimation: successAnimation.default,
      warningAnimation: warningAnimation.default,
      showImportKvkModal: false,
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
      companyMissingFields: 'main/getCompanyMissingFields',
      targetData: 'main/getCompanyRequiredData',
      currentUser: 'main/getCurrentUser',
      accountantCompanyMissingFields: 'main/getAccountantCompanyMissingFields',
      accountantTargetData: 'main/getAccountantCompanyRequiredData',
      isWaitingForResponse: 'main/isWaitingForResponse',
      isAccountantDashboard: 'main/isAccountantDashboard',
    }),
    progress() {
      let progress
      if (this.isAccountantDashboard) {
        progress = Math.round(
          ((this.accountantTargetData.length - this.accountantCompanyMissingFields.length) /
            this.accountantTargetData.length) *
            100
        )
      } else {
        progress = Math.round(
          ((this.targetData.length - this.companyMissingFields.length) / this.targetData.length) * 100
        )
      }
      return progress
    },
    isIncomplete() {
      let incomplete
      if (this.isAccountantDashboard) {
        incomplete = this.accountantCompanyMissingFields.length > 0
      } else {
        incomplete = this.companyMissingFields.length > 0
      }
      return incomplete
    },
  },
  created() {},
  mounted() {
    this.setupWidget()
    this.setObjectStatus(this.widget.config)
  },
  methods: {
    ...mapActions({
      loadCurrentCompany: 'main/loadCurrentCompany',
    }),
    ...mapMutations({
      openOnboardingWizard: 'onboarding/OPEN_ONBOARDING_WIZARD',
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    setConfig(config) {
      if (config.status) this.setObjectStatus(config)
      this.$emit('save-config', this.config)
    },
    setObjectStatus(config) {
      if (config.status) {
        this.config.status = config.status && config.status !== 'all' ? config.status : ''
      }
      this.setFilters()
    },
    setFilters(status = false) {
      this.filters.status = status ? (status === 'all' ? '' : status) : this.config.status
      this.refreshData()
    },
    refreshData() {
      this.getData()
    },
    updateFilters(status) {
      this.filters.status = status === 'all' ? '' : status
      this.setFilters(status)
    },
    getData() {
      this.loading = true
      this.loadCurrentCompany().then(() => {
        this.loading = false
      })
    },
    setupWidget() {
      this.title = this.$gettext('Company profile')
    },
    removeWidget() {
      this.$emit('widget-event', { event: 'remove' })
      setTimeout(() => {
        this.$emit('widget-event', { event: 'update', target: 'activities-timeline-widget' })
      }, 100)
    },
    widgetClick() {
      if (this.isAccountantDashboard) {
        if (!this.loading && this.accountantCompanyMissingFields.length === 0) {
          this.gotoAccountantSettings()
        }
      } else {
        if (!this.loading && this.companyMissingFields.length === 0) {
          this.gotoSettings()
        } else {
          this.openWizard()
        }
      }
    },
    gotoSettings() {
      window.location.href = '/en/accounts/companies/'
    },
    gotoAccountantSettings() {
      this.$router.push({ name: 'AccountantCompanySettings' })
    },
    openWizard() {
      if (this.isAccountantDashboard) {
        this.gotoAccountantSettings()
      } else {
        this.openOnboardingWizard()
      }
    },
    sendVerificationEmail() {
      this.setWaitingForResponse(true)
      ProfileService.sendVerificationEmail()
        .then(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Email sent'),
            text: this.$gettext('A verification email has been sent, check your email address to verify your email.'),
            variant: 'success',
            duration: 4,
          })
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Failed to send email'),
            text: this.$gettext('We failed to send you an email, please try again later.'),
            variant: 'danger',
            duration: 4,
          })
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
  },
}
</script>

<style lang="scss">
.loading-spinner-button {
  .loading-spinner {
    width: auto !important;
  }
}

.widget-wrapper.company-profile {
  cursor: pointer;
  &.incomplete {
    border: 5px solid #ffcd1f !important;
  }
}

.company-profile {
  .company-profile-wrap {
    display: flex;
    align-content: center;
    justify-content: center;
    width: 100%;
  }
  .complete-content-wrap {
    width: 100%;
    text-align: center;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    line-height: 1.2;
    &--logo {
      img {
        max-width: 100px;
        max-height: 100px;
      }
    }
    &--title {
      
    }
    &--description {
      font-size: 14px;
      color: #0000007d;
      margin-top: 5px;
    }
  }
  &.accountant-profile {
    .complete-content-wrap {
      &--logo {
        img {
          max-width: 90px;
          max-height: 90px;
        }
      }
      &--title {
        font-size: 20px;
      }
    }
  }
  .incomplete-content-wrap {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    line-height: 1;
    .incomplete-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      text-align: center;
      justify-content: flex-end;
      align-items: center;
      .incomplete-icon-text {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      h4 {
        margin: 0;
        margin-bottom: 0.5rem;
      }
      p {
        display: none;
        font-size: 14px;
        color: $colorUnassigned;
      }
      .btn {
        white-space: nowrap;
      }
    }

    @media (min-width: '992px') {
      flex-direction: row;
      .incomplete-content {
        p {
          display: block;
        }
        padding-left: 0.25rem;
      }
    }
  }

  .anim-warning {
    width: 100px !important;
    height: 100px !important;
    margin: -1rem !important;
  }
  .anim-success {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    position: absolute;
    top: 0;
    left: 0;
  }
  .remove-widget-link {
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
